import { useEffect, useRef, useCallback, useState } from "react";
import { googleClientId } from "@/config/authConfig";

/**
 * Google Identity Services types
 */
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              width?: number;
            }
          ) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface UseGoogleAuthOptions {
  onSuccess?: (credential: string) => void | Promise<void>;
  onError?: (error: string) => void;
  enableOneTap?: boolean;
  autoSelect?: boolean;
}

/**
 * Custom hook for Google Authentication using Google Identity Services API
 * Provides optimized, reusable Google Sign-In functionality with One Tap support
 */
export const useGoogleAuth = (options: UseGoogleAuthOptions = {}) => {
  const {
    onSuccess,
    onError,
    enableOneTap = true,
    autoSelect = false,
  } = options;

  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const hasRenderedButton = useRef(false);
  const scriptLoadAttempted = useRef(false);

  /**
   * Handle Google credential response
   */
  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        if (onSuccess) {
          await onSuccess(response.credential);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Authentication failed";
        console.error("Google authentication error:", errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    },
    [onSuccess, onError]
  );

  /**
   * Load Google Identity Services script
   */
  const loadGoogleScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[data-google-identity-script="true"]'
      );

      if (existingScript) {
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }

        // Wait for existing script to load
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(), { once: true });
        return;
      }

      // Create and load new script
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.dataset.googleIdentityScript = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
      document.body.appendChild(script);
    });
  }, []);

  /**
   * Initialize Google Identity Services
   */
  const initializeGoogle = useCallback(() => {
    if (!window.google?.accounts?.id) {
      console.error("Google Identity Services script not loaded yet.");
      return;
    }

    if (!googleClientId || googleClientId === "YOUR_GOOGLE_CLIENT_ID") {
      console.warn("Google Client ID not configured. Please set VITE_GOOGLE_CLIENT_ID in your .env file");
      return;
    }

    if (isInitialized) {
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
        auto_select: autoSelect,
        cancel_on_tap_outside: true,
      });

      setIsInitialized(true);
    } catch (error) {
      console.error("Failed to initialize Google Identity Services:", error);
      if (onError) {
        onError("Failed to initialize Google authentication");
      }
    }
  }, [googleClientId, handleCredentialResponse, autoSelect, isInitialized, onError]);

  /**
   * Render Google Sign-In button
   */
  const renderButton = useCallback(
    (element: HTMLElement | null, config?: {
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "large" | "medium" | "small";
      text?: "signin_with" | "signup_with" | "continue_with" | "signin";
      width?: number;
    }) => {
      if (!element || !window.google?.accounts?.id || !isInitialized) {
        return;
      }

      // Clear previous button if exists
      element.innerHTML = "";

      const buttonConfig = {
        theme: config?.theme || "outline",
        size: config?.size || "large",
        text: config?.text || "continue_with",
        width: config?.width || element.clientWidth || 384,
      };

      try {
        window.google.accounts.id.renderButton(element, buttonConfig);
        hasRenderedButton.current = true;
      } catch (error) {
        console.error("Failed to render Google button:", error);
      }
    },
    [isInitialized, onError]
  );

  /**
   * Show One Tap prompt
   */
  const showOneTap = useCallback(() => {
    if (!window.google?.accounts?.id || !isInitialized || !enableOneTap) {
      return;
    }

    try {
      window.google.accounts.id.prompt();
    } catch (error) {
      console.error("Failed to show One Tap:", error);
    }
  }, [isInitialized, enableOneTap]);

  /**
   * Load Google script on mount
   */
  useEffect(() => {
    if (scriptLoadAttempted.current) {
      return;
    }

    scriptLoadAttempted.current = true;

    loadGoogleScript()
      .then(() => {
        setIsScriptLoaded(true);
      })
      .catch((error) => {
        console.error("Failed to load Google Identity Services:", error);
        if (onError) {
          onError("Unable to load Google login. Please refresh the page.");
        }
      });
  }, [loadGoogleScript, onError]);

  /**
   * Initialize Google when script is loaded
   */
  useEffect(() => {
    if (isScriptLoaded && !isInitialized) {
      initializeGoogle();
    }
  }, [isScriptLoaded, isInitialized, initializeGoogle]);

  /**
   * Render button when initialized and ref is available
   */
  useEffect(() => {
    if (isInitialized && buttonRef.current && !hasRenderedButton.current) {
      renderButton(buttonRef.current);
    }
  }, [isInitialized, renderButton]);

  /**
   * Show One Tap prompt when initialized
   */
  useEffect(() => {
    if (isInitialized && enableOneTap) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        showOneTap();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isInitialized, enableOneTap, showOneTap]);

  return {
    buttonRef,
    isReady: isInitialized,
    renderButton,
    showOneTap,
  };
};

